import { useEffect, useMemo } from "react";
import hash from "object-hash";
import { Nullable } from "src/js/types/Nullable";
import { useLocalStorageReducer } from "react-migration/lib/persistence/state/useLocalStorageReducer";
import type { ConstraintLayerConfig, ConstraintsLayer } from "../../types";
import { isSingleConstraintLayer } from "../../types";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { getCategoryByIdQuery } from "../../getCategoryByIdQuery";
import { DISPLAY_BY_CATEGORY } from "../../constants";
import { CONSTRAINT_STATUSES, type ConstraintsStore, type LayerState } from "./types";
import { setCategoryTree, setVisibleCategories, setLayerConfigHash } from "./actions";
import { constraintsStoreReducer } from "./reducer";
import { getDefaultVisibleCategoriesRecursive, getCategoryIdRecursive, pruneTree } from "./categoryTree";
import { TreeStructure } from "react-migration/components/CheckboxTree";

function getLayersFromLocalStorage(localStorageKey: string): Nullable<ConstraintsStore["layers"]> {
  try {
    return JSON.parse(localStorage.getItem(localStorageKey)!).layers ?? null;
  } catch {
    return null;
  }
}

function shouldInitialiseLayerState(layerId: string, localStorageKey: string, currentLayerConfigHash: string) {
  const layerStateFromStore = getLayersFromLocalStorage(localStorageKey);
  const layerConfigChanged = currentLayerConfigHash !== layerStateFromStore?.[layerId]?.layerConfigHash;

  return !layerStateFromStore || layerConfigChanged;
}

export function useConstraintsStore(id: string, layers: ConstraintsLayer[]) {
  const translation = useTranslation();
  const [constraintsStore, dispatch, localStorageKey] = useLocalStorageReducer(
    id,
    constraintsStoreReducer,
    createInitialState(layers),
    { omit: layers.map((l) => `layers.${l.id}.categoryTree`) }
  );

  const layerConfigHashHashById = useMemo(
    () => Object.fromEntries(layers.map((layer) => [layer.id, hash(layer.layerConfig!)])),
    [layers]
  );

  useEffect(
    function initialiseConstraintsStore() {
      async function initialise() {
        // TODO: don't initialise layers sequentially, do so in parallel
        for (const layer of layers) {
          if (!layer.layerConfig) return;

          const { categorySchema } = layer.layerConfig;

          const rootCategories = isSingleConstraintLayer(layer.layerConfig!)
            ? [layer.layerConfig.rootCategory]
            : layer.layerConfig.rootCategories;

          try {
            const categories = await Promise.all(
              rootCategories.map((rootCategory) => getCategoryByIdQuery(rootCategory))
            );

            const categoryTrees = categories
              .map((category) => pruneTree(category, categorySchema, translation))
              .filter((tree): tree is TreeStructure => tree != null);

            if (categoryTrees.length) {
              if (categoryTrees.length === 1) {
                dispatch(setCategoryTree(layer.id, categoryTrees[0]));
              } else {
                dispatch(
                  setCategoryTree(layer.id, {
                    id: layer.id,
                    display_name: layer.title,
                    childNodes: categoryTrees.map(
                      (tree) =>
                        ({
                          id: tree.id,
                          display_name: tree.display_name,
                          childNodes: tree.childNodes,
                        } satisfies TreeStructure)
                    ),
                  })
                );
              }

              if (shouldInitialiseLayerState(layer.id, localStorageKey, layerConfigHashHashById[layer.id])) {
                const defaultVisibleCategories = categoryTrees.flatMap((tree) =>
                  getDefaultVisibleCategoriesRecursive(tree, categorySchema)
                );
                dispatch(setVisibleCategories(layer.id, defaultVisibleCategories));

                dispatch(setLayerConfigHash(layer.id, hash(layer.layerConfig)));
              } else {
                const allPossibleCategories = categoryTrees.flatMap(getCategoryIdRecursive);
                const visibleCategories = constraintsStore.layers[layer.id].visibleCategories;
                const invalidCategories = visibleCategories.filter(
                  (category) => !allPossibleCategories.includes(category)
                );

                if (invalidCategories.length) {
                  dispatch(
                    setVisibleCategories(
                      layer.id,
                      visibleCategories.filter((category) => !invalidCategories.includes(category))
                    )
                  );
                }
              }
            }
          } catch (error) {
            console.error("Unable to initialise Constraints Store for root categories:", rootCategories);
          }
        }
      }

      initialise();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [layers, dispatch, localStorageKey, translation]
  );

  return [constraintsStore, dispatch] as const;
}

export const createInitialLayerState = (layerConfig?: ConstraintLayerConfig): LayerState => {
  return {
    visibleCategories: [],
    hiddenDesignationAttributes: {},
    layerConfigHash: null,
    categoryTree: null,
    hiddenStatuses: layerConfig?.statusSchema
      ? CONSTRAINT_STATUSES.filter((status) => !layerConfig?.statusSchema?.includes(status))
      : [],
    displayBy: layerConfig?.displayBy?.defaultDisplayBy || DISPLAY_BY_CATEGORY,
  };
};

export const initialState = { layers: {} };

export function createInitialState(layers: ConstraintsLayer[]): ConstraintsStore {
  return {
    ...initialState,
    layers: Object.fromEntries(layers.map((layer) => [layer.id, createInitialLayerState(layer.layerConfig)])),
  };
}
